import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, User, FileText, Trash2, MessageSquare, Image as ImageIcon, File, XCircle, ArrowLeft, ZoomIn, Download, Navigation, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { cloudApi, getApiBaseUrl, chatApi } from '../../services/cloudApi';
import ImageModal from './ImageModal';
import { getPlusEmailColor } from '../../utils/userDisplay';
import { getIntlLocale } from '../../utils/dateLocale';

// Helper function to get user initials
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
};

const FriendInfoModal = ({ isOpen, onClose, friend, asPage = false }) => {
  const { t, i18n } = useTranslation('chat');
  const dateLocaleTag = getIntlLocale(i18n.language);
  const { deleteFriend, deleteMessages } = useChat();
  const { authToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [mediaFiles, setMediaFiles] = useState({ images: [], documents: [] });
  const [activeTab, setActiveTab] = useState('info'); // 'info' or 'media'
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null); // { x, y, item, type }
  const contextMenuRef = useRef(null);

  // Delete media item confirmation
  const [showMediaDeleteConfirm, setShowMediaDeleteConfirm] = useState(false);
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null);

  // Document preview state
  const [docPreview, setDocPreview] = useState(null); // { url, fileName }

  // Load friend data
  useEffect(() => {
    if (isOpen && friend && authToken) {
      loadFriendData();
    }
  }, [isOpen, friend?.id, authToken]);

  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu(null);
      }
    };
    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenu]);

  const loadFriendData = async () => {
    if (!friend || !authToken) return;
    setIsLoading(true);
    try {
      await loadMediaFiles();
    } catch (error) {
      console.error('Load friend data error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMediaFiles = async () => {
    if (!friend || !authToken) return;
    try {
      const apiBaseUrl = await getApiBaseUrl(); // await required — returns Promise
      const baseUrl = apiBaseUrl.replace('/api', '');
      const messagesResult = await fetch(
        `${baseUrl}/api/chat/messages/${friend.id}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await messagesResult.json();

      if (result?.ok && result?.messages) {
        const images = result.messages.filter((msg) =>
          (msg.type === 'image' || msg.message_type === 'image') && msg.file_url
        ).map((msg) => {
          let fileUrl = msg.file_url;
          if (fileUrl && !fileUrl.startsWith('http')) {
            fileUrl = `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
          }
          return {
            id: msg.id,
            url: fileUrl,
            createdAt: msg.created_at || msg.createdAt || msg.timestamp,
          };
        });

        const documents = result.messages.filter((msg) =>
          (msg.type === 'file' || msg.message_type === 'file' || msg.type === 'document') && msg.file_url
        ).map((msg) => {
          let fileUrl = msg.file_url;
          if (fileUrl && !fileUrl.startsWith('http')) {
            fileUrl = `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
          }
          return {
            id: msg.id,
            url: fileUrl,
            fileName: msg.file_name || msg.fileName || msg.content || t('forwardModal.typeFallback.file'),
            createdAt: msg.created_at || msg.createdAt || msg.timestamp,
          };
        });

        setMediaFiles({ images, documents });
      }
    } catch (error) {
      console.error('Load media files error:', error);
    }
  };

  const handleDeleteFriend = async () => {
    if (!friend) return;
    await deleteFriend(friend.id);
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleClearMessages = async () => {
    if (!friend) return;
    await deleteMessages(friend.id);
    setShowClearConfirm(false);
    onClose();
  };

  // Context menu for media items
  const handleContextMenu = (e, item, type) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, item, type });
  };

  // "Mesaja Git" — close modal, navigate to chat, scroll to message
  const handleGoToMessage = (item) => {
    setContextMenu(null);
    onClose();
    // Dispatch event for ChatPage to handle
    window.dispatchEvent(new CustomEvent('notesplus-scroll-to-message-dm', {
      detail: { friendId: friend.id, messageId: item.id },
    }));
  };

  // Delete media message confirmation
  const handleMediaDeleteRequest = (item) => {
    setContextMenu(null);
    setPendingDeleteItem(item);
    setShowMediaDeleteConfirm(true);
  };

  const handleMediaDeleteConfirm = async () => {
    if (!pendingDeleteItem) return;
    try {
      await chatApi.deleteMessage({ token: authToken, messageId: pendingDeleteItem.id });
      // Remove from local state
      setMediaFiles((prev) => ({
        images: prev.images.filter((img) => img.id !== pendingDeleteItem.id),
        documents: prev.documents.filter((doc) => doc.id !== pendingDeleteItem.id),
      }));
    } catch (err) {
      console.error('Delete message error:', err);
    }
    setShowMediaDeleteConfirm(false);
    setPendingDeleteItem(null);
  };

  // Get file extension for icon color
  const getFileExt = (fileName) => {
    if (!fileName) return '';
    return fileName.split('.').pop()?.toLowerCase() || '';
  };

  const getExtColor = (ext) => {
    const colors = { pdf: 'text-red-400', doc: 'text-blue-400', docx: 'text-blue-400', xls: 'text-green-400', xlsx: 'text-green-400', ppt: 'text-orange-400', pptx: 'text-orange-400', zip: 'text-yellow-400', rar: 'text-yellow-400' };
    return colors[ext] || 'text-[var(--color-text-muted)]';
  };

  // Render content (shared between modal and page)
  const renderContent = () => (
    <>
      {/* Tabs */}
      <div className="flex border-b border-white/10">
        <button
          onClick={() => setActiveTab('info')}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'info'
              ? 'text-[#3d8ef0] border-b-2 border-[#3d8ef0]'
              : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
          }`}
        >
          {t('friendInfoModal.tabInfo')}
        </button>
        <button
          onClick={() => setActiveTab('media')}
          className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'media'
              ? 'text-[#3d8ef0] border-b-2 border-[#3d8ef0]'
              : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
          }`}
        >
          {t('friendInfoModal.tabMedia')} {(mediaFiles.images.length + mediaFiles.documents.length) > 0 && (
            <span className="ml-1 text-xs bg-[#2563eb]/20 text-[#3d8ef0] px-1.5 py-0.5 rounded-full">
              {mediaFiles.images.length + mediaFiles.documents.length}
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'info' ? (
          <div className="space-y-4">
            {/* Avatar and Name */}
            <div className="flex flex-col items-center text-center pt-4 pb-2">
              {(() => {
                const avatarSrc = friend.avatarUrl || friend.avatar_url || null;
                return (
                  <div
                    className={`w-24 h-24 rounded-full bg-[#2563eb]/10 flex items-center justify-center text-2xl font-bold text-[#3d8ef0] mb-3 relative group ${avatarSrc ? 'cursor-pointer' : ''}`}
                    onClick={() => {
                      if (avatarSrc) {
                        setSelectedImageUrl(avatarSrc);
                        setShowImageModal(true);
                      }
                    }}
                  >
                    {avatarSrc ? (
                      <>
                        <img
                          src={avatarSrc}
                          alt={friend.email}
                          className="w-full h-full rounded-full object-cover"
                        />
                        <div className="absolute inset-0 rounded-full bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                          <ZoomIn className="w-6 h-6 text-white" />
                        </div>
                      </>
                    ) : (
                      friend.email?.split('@')[0]?.[0]?.toUpperCase() || '?'
                    )}
                  </div>
                );
              })()}
              <h3 className={`text-xl font-bold mb-1 ${getPlusEmailColor(friend)}`}>
                {friend.email}
              </h3>
            </div>

            <div className="px-6">
              {/* Info List */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--color-bg-muted)]">
                  <Mail className="w-5 h-5 text-[var(--color-text-muted)]" />
                  <div className="flex-1">
                    <p className="text-xs text-[var(--color-text-muted)]">{t('friendInfoModal.emailLabel')}</p>
                    <p className={`text-sm font-medium ${getPlusEmailColor(friend)}`}>
                      {friend.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-white/10">
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors text-sm"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>{t('friendInfoSidebar.clearChat')}</span>
                </button>

                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>{t('friendInfoSidebar.deleteFriend')}</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6 p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-[#3d8ef0] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Images Section */}
                <div>
                  <h3 className="text-sm font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-3 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    {t('friendInfoModal.photosLabel', { count: mediaFiles.images.length })}
                  </h3>
                  {mediaFiles.images.length > 0 ? (
                    <div className="grid grid-cols-3 gap-1.5">
                      {mediaFiles.images.map((image) => (
                        <div
                          key={image.id}
                          className="aspect-square rounded-lg overflow-hidden bg-[var(--color-bg-muted)] cursor-pointer relative group"
                          onClick={() => {
                            setSelectedImageUrl(image.url);
                            setShowImageModal(true);
                          }}
                          onContextMenu={(e) => handleContextMenu(e, image, 'image')}
                        >
                          <img
                            src={image.url}
                            alt={t('friendInfoModal.photoAlt')}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <ZoomIn className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[var(--color-text-muted)] text-center py-6 bg-[var(--color-bg-base)] rounded-xl border border-white/[0.05]">
                      {t('friendInfoModal.noPhotosYet')}
                    </p>
                  )}
                </div>

                {/* Documents Section */}
                <div>
                  <h3 className="text-sm font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-3 flex items-center gap-2">
                    <File className="w-4 h-4" />
                    {t('friendInfoModal.documentsLabel', { count: mediaFiles.documents.length })}
                  </h3>
                  {mediaFiles.documents.length > 0 ? (
                    <div className="space-y-2">
                      {mediaFiles.documents.map((doc) => {
                        const ext = getFileExt(doc.fileName);
                        return (
                          <div
                            key={doc.id}
                            className="flex items-center gap-3 p-3 rounded-xl bg-[var(--color-bg-muted)] hover:bg-[var(--color-bg-elevated)] transition-colors cursor-pointer border border-white/[0.05]"
                            onClick={() => setDocPreview(doc)}
                            onContextMenu={(e) => handleContextMenu(e, doc, 'document')}
                          >
                            <div className="w-10 h-10 rounded-lg bg-[#161f31] flex items-center justify-center flex-shrink-0">
                              <File className={`w-5 h-5 ${getExtColor(ext)}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                                {doc.fileName}
                              </p>
                              <p className="text-xs text-[var(--color-text-muted)]">
                                {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString(dateLocaleTag) : ''}
                              </p>
                            </div>
                            <Eye className="w-4 h-4 text-[var(--color-text-muted)] flex-shrink-0" />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-[var(--color-text-muted)] text-center py-6 bg-[var(--color-bg-base)] rounded-xl border border-white/[0.05]">
                      {t('friendInfoModal.noDocumentsYet')}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );

  if (!isOpen || !friend) return null;

  // Shared overlay elements (context menu, doc preview, delete confirm)
  const renderOverlays = () => (
    <>
      {/* Context Menu */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="fixed z-[200000] bg-[var(--color-bg-base)] border border-white/[0.08] rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.6)] py-1 min-w-[160px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[var(--color-text-secondary)] hover:bg-white/[0.05] transition-colors"
            onClick={() => handleGoToMessage(contextMenu.item)}
          >
            <Navigation className="w-4 h-4 text-[#3d8ef0]" />
            {t('friendInfoModal.goToMessage')}
          </button>
          <button
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-white/[0.05] transition-colors"
            onClick={() => handleMediaDeleteRequest(contextMenu.item)}
          >
            <Trash2 className="w-4 h-4" />
            {t('common:delete')}
          </button>
        </div>
      )}

      {/* Media Delete Confirmation */}
      {showMediaDeleteConfirm && (
        <div className="fixed inset-0 z-[200001] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowMediaDeleteConfirm(false)} />
          <div className="relative bg-[var(--color-bg-base)] border border-white/[0.08] rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-[var(--color-text-primary)] mb-2">{t('friendInfoModal.deleteMessageTitle')}</h3>
            <p className="text-sm text-[var(--color-text-muted)] mb-5">
              {t('friendInfoModal.deleteMediaConfirmMessage')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowMediaDeleteConfirm(false)}
                className="flex-1 px-4 py-2 bg-[#161f31] text-[var(--color-text-primary)] rounded-lg hover:bg-[#24354f] transition-colors text-sm"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={handleMediaDeleteConfirm}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
              >
                {t('common:delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {docPreview && (
        <div className="fixed inset-0 z-[200001] flex flex-col" style={{ background: 'rgba(5,9,18,0.97)' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.08]">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setDocPreview(null)}
                className="p-2 rounded-lg hover:bg-white/[0.07] text-[var(--color-text-muted)] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <span className="text-sm font-medium text-[var(--color-text-primary)] truncate max-w-xs">{docPreview.fileName}</span>
            </div>
            <a
              href={docPreview.url}
              download={docPreview.fileName}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-[#2563eb] hover:bg-[#3d8ef0] text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Download className="w-4 h-4" />
              {t('friendInfoModal.download')}
            </a>
          </div>
          {/* Preview area */}
          <div className="flex-1 overflow-hidden p-4">
            <iframe
              src={docPreview.url}
              title={docPreview.fileName}
              className="w-full h-full rounded-xl border border-white/[0.08] bg-white"
              sandbox="allow-scripts allow-same-origin"
            />
          </div>
        </div>
      )}

      {/* Image Modal */}
      <ImageModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setSelectedImageUrl(null);
        }}
        imageUrl={selectedImageUrl}
      />
    </>
  );

  // If asPage is true, render as full page (WhatsApp style)
  if (asPage) {
    return (
      <>
        <motion.div
          initial={{ opacity: 0, x: '100%' }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: '100%' }}
          transition={{ duration: 0.2 }}
          className="h-full w-full flex flex-col bg-[var(--color-bg-base)] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center gap-3 p-4 border-b border-white/10 bg-[var(--color-bg-base)]">
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
              {t('friendInfoModal.title')}
            </h2>
          </div>
          {/* Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {renderContent()}
          </div>
        </motion.div>
        {renderOverlays()}
        {/* Friend delete/clear confirmation (asPage) */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-[200001] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={() => setShowDeleteConfirm(false)} />
            <div className="relative bg-[var(--color-bg-base)] border border-white/[0.08] rounded-2xl shadow-2xl p-6 max-w-sm w-full">
              <h3 className="text-lg font-bold text-[var(--color-text-primary)] mb-2">{t('friendInfoSidebar.deleteFriend')}</h3>
              <p className="text-sm text-[var(--color-text-muted)] mb-5">{t('friendInfoSidebar.deleteFriendConfirmMessage', { email: friend.email })}</p>
              <div className="flex gap-3">
                <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 px-4 py-2 bg-[#161f31] text-[var(--color-text-primary)] rounded-lg hover:bg-[#24354f] transition-colors text-sm">{t('common:cancel')}</button>
                <button onClick={handleDeleteFriend} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium">{t('common:delete')}</button>
              </div>
            </div>
          </div>
        )}
        {showClearConfirm && (
          <div className="fixed inset-0 z-[200001] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={() => setShowClearConfirm(false)} />
            <div className="relative bg-[var(--color-bg-base)] border border-white/[0.08] rounded-2xl shadow-2xl p-6 max-w-sm w-full">
              <h3 className="text-lg font-bold text-[var(--color-text-primary)] mb-2">{t('friendInfoSidebar.clearChat')}</h3>
              <p className="text-sm text-[var(--color-text-muted)] mb-5">{t('friendInfoSidebar.clearChatConfirmMessage')}</p>
              <div className="flex gap-3">
                <button onClick={() => setShowClearConfirm(false)} className="flex-1 px-4 py-2 bg-[#161f31] text-[var(--color-text-primary)] rounded-lg hover:bg-[#24354f] transition-colors text-sm">{t('common:cancel')}</button>
                <button onClick={handleClearMessages} className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm font-medium">{t('friendInfoModal.clearAction')}</button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100000]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[100001] flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[var(--color-bg-base)] rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <h2 className="text-xl font-bold text-[var(--color-text-primary)]">
                  {t('friendInfoModal.title')}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-hidden flex flex-col">
                {renderContent()}
              </div>
            </div>
          </motion.div>

          {/* Overlays rendered outside modal stacking context */}
          {renderOverlays()}

          {/* Delete Friend Confirmation */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 z-[200001] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/60" onClick={() => setShowDeleteConfirm(false)} />
              <div className="relative bg-[var(--color-bg-base)] border border-white/[0.08] rounded-2xl shadow-2xl p-6 max-w-sm w-full">
                <h3 className="text-lg font-bold text-[var(--color-text-primary)] mb-2">{t('friendInfoSidebar.deleteFriend')}</h3>
                <p className="text-sm text-[var(--color-text-muted)] mb-5">{t('friendInfoSidebar.deleteFriendConfirmMessage', { email: friend.email })}</p>
                <div className="flex gap-3">
                  <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 px-4 py-2 bg-[#161f31] text-[var(--color-text-primary)] rounded-lg hover:bg-[#24354f] transition-colors text-sm">{t('common:cancel')}</button>
                  <button onClick={handleDeleteFriend} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium">{t('common:delete')}</button>
                </div>
              </div>
            </div>
          )}

          {/* Clear Messages Confirmation */}
          {showClearConfirm && (
            <div className="fixed inset-0 z-[200001] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/60" onClick={() => setShowClearConfirm(false)} />
              <div className="relative bg-[var(--color-bg-base)] border border-white/[0.08] rounded-2xl shadow-2xl p-6 max-w-sm w-full">
                <h3 className="text-lg font-bold text-[var(--color-text-primary)] mb-2">{t('friendInfoSidebar.clearChat')}</h3>
                <p className="text-sm text-[var(--color-text-muted)] mb-5">{t('friendInfoSidebar.clearChatConfirmMessage')}</p>
                <div className="flex gap-3">
                  <button onClick={() => setShowClearConfirm(false)} className="flex-1 px-4 py-2 bg-[#161f31] text-[var(--color-text-primary)] rounded-lg hover:bg-[#24354f] transition-colors text-sm">{t('common:cancel')}</button>
                  <button onClick={handleClearMessages} className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm font-medium">{t('friendInfoModal.clearAction')}</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};

export default FriendInfoModal;
